import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { BarChart3, Building2, TrendingUp, Settings, Menu, Database } from 'lucide-react';

const navigation = [
  {
    name: 'Dashboard',
    href: '/',
    icon: BarChart3,
  },
  {
    name: 'Organizations',
    href: '/organizations',
    icon: Building2,
  },
  {
    name: 'Analytics',
    href: '/analytics', 
    icon: TrendingUp,
  },
  {
    name: 'Processing',
    href: '/processing',
    icon: Database,
  },
];

export default function Layout({ children }) {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Desktop Sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-64 lg:flex-col">
        <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white border-r border-slate-200 px-6 pb-4">
          <div className="flex h-16 shrink-0 items-center">
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-slate-900 rounded flex items-center justify-center">
                <span className="text-white font-bold text-sm">ED</span>
              </div>
              <div>
                <h2 className="text-lg font-black tracking-tight text-slate-900">
                  Economic Dev
                </h2>
                <p className="text-xs text-slate-600 -mt-1">Donor Intelligence</p>
              </div>
            </Link>
          </div>
          <nav className="flex flex-1 flex-col">
            <ul role="list" className="flex flex-1 flex-col gap-y-7">
              <li>
                <ul role="list" className="-mx-2 space-y-1">
                  {navigation.map((item) => {
                    const isActive = location.pathname === item.href;
                    return (
                      <li key={item.name}>
                        <Link
                          to={item.href}
                          className={`
                            group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-medium transition-smooth
                            ${
                              isActive
                                ? 'bg-slate-100 text-slate-900'
                                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                            }
                          `}
                          data-testid={`nav-${item.name.toLowerCase()}`}
                        >
                          <item.icon
                            className={`
                              h-5 w-5 shrink-0
                              ${
                                isActive
                                  ? 'text-slate-700'
                                  : 'text-slate-400 group-hover:text-slate-600'
                              }
                            `}
                          />
                          {item.name}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      {/* Mobile Navigation */}
      <div className="lg:hidden">
        <div className="flex h-16 items-center gap-x-4 bg-white border-b border-slate-200 px-4 shadow-sm glass-nav sticky top-0 z-40">
          <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="sm" data-testid="mobile-menu-btn">
                <span className="sr-only">Open sidebar</span>
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-64">
              <div className="flex items-center space-x-2 mb-6">
                <div className="w-8 h-8 bg-slate-900 rounded flex items-center justify-center">
                  <span className="text-white font-bold text-sm">ED</span>
                </div>
                <div>
                  <h2 className="text-lg font-black tracking-tight text-slate-900">
                    Economic Dev
                  </h2>
                  <p className="text-xs text-slate-600 -mt-1">Donor Intelligence</p>
                </div>
              </div>
              <nav className="space-y-1">
                {navigation.map((item) => {
                  const isActive = location.pathname === item.href;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`
                        group flex items-center gap-x-3 rounded-md p-2 text-sm font-medium transition-smooth
                        ${
                          isActive
                            ? 'bg-slate-100 text-slate-900'
                            : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                        }
                      `}
                      onClick={() => setIsMobileMenuOpen(false)}
                      data-testid={`mobile-nav-${item.name.toLowerCase()}`}
                    >
                      <item.icon
                        className={`
                          h-5 w-5 shrink-0
                          ${
                            isActive
                              ? 'text-slate-700'
                              : 'text-slate-400 group-hover:text-slate-600'
                          }
                        `}
                      />
                      {item.name}
                    </Link>
                  );
                })}
              </nav>
            </SheetContent>
          </Sheet>
          
          <div className="flex items-center space-x-2">
            <div className="w-6 h-6 bg-slate-900 rounded flex items-center justify-center">
              <span className="text-white font-bold text-xs">ED</span>
            </div>
            <h1 className="text-lg font-black tracking-tight text-slate-900">
              Economic Dev
            </h1>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="lg:pl-64">
        <main>
          {children}
        </main>
      </div>
    </div>
  );
}
